import type {
    MongoAbility,
    InferSubjects,
    ForcedSubject,
    FieldMatcher,
} from '@casl/ability'
import type { Action, Capability, Subject } from './types'
import { type Session } from './types'
import { $or } from '@ucast/mongo2js'
import {
    AbilityBuilder,
    createMongoAbility,
    buildMongoQueryMatcher,
    createAliasResolver,
    subject as caslSubject,
} from '@casl/ability'
import { organizationCapabilitiesByRole, userCapabilitiesByRole } from './roles'
import DefaultCapabilities from './roles/User'
import { createAccessControl } from 'better-auth/plugins'
import {
    adminAc,
    defaultStatements,
    memberAc,
    ownerAc,
} from 'better-auth/plugins/organization/access'

export const OrganizationRoles = {
    admin: 'admin',
    owner: 'owner',
    member: 'member',
    user: 'user',
} as const

export const UserRoles = {
    admin: 'admin',
    user: 'user',
} as const

// action alias
const resolveAction = createAliasResolver({
    modify: ['read', 'update', 'delete'],
})

// subject
export type AbilitySubject =
    | InferSubjects<'all' | Subject, true>
    | ForcedSubject<InferSubjects<'all' | Subject, true>>
export type AppAbility = MongoAbility<[Action, AbilitySubject]>

// matcher
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const conditionsMatcher = buildMongoQueryMatcher({ $or } as Record<string, any>)
const fieldMatcher: FieldMatcher = (fields) => (field) => fields.includes(field)

export const isValidOrganizationRole = (
    role?: string | null,
): role is keyof typeof OrganizationRoles => {
    if (!role) {
        return false
    }
    return role in OrganizationRoles
}

export const isValidUserRole = (
    role?: string | null,
): role is keyof typeof UserRoles => {
    if (!role) {
        return false
    }
    return role in UserRoles
}

export const getOrganizationRoleCapabilities = (
    currentSession?: Session | null,
): Capability[] => {
    const organizationRole = currentSession?.session?.organizationRole
    if (!isValidOrganizationRole(organizationRole)) {
        return DefaultCapabilities(currentSession)
    }
    const capabilities = organizationCapabilitiesByRole[organizationRole]
    if (typeof capabilities === 'function') {
        return capabilities(currentSession)
    }
    return capabilities
}

export const getUserRoleCapabilities = (
    currentSession?: Session | null,
): Capability[] => {
    const userRole = currentSession?.user?.role
    if (!isValidUserRole(userRole)) {
        return DefaultCapabilities(currentSession)
    }
    const capabilities = userCapabilitiesByRole[userRole]
    if (typeof capabilities === 'function') {
        return capabilities(currentSession)
    }
    return capabilities
}

/**
 * Builds the ability of one caller: the session's roles, or the guest rules
 * when there is no session. Every call returns a new instance and this package
 * keeps none: a server judges many callers at once, so an instance shared
 * between them is judged by whoever wrote it last. The backend builds one per
 * request (`request.ability`), the Platform MCP one per tool call, and the
 * frontend keeps its own for the single user of the tab.
 */
export const buildAbilityForSession = (
    currentSession?: Session | null,
): AppAbility => {
    const { can, build, cannot } = new AbilityBuilder<AppAbility>(
        createMongoAbility,
    )
    const organizationRoleCapabilities =
        getOrganizationRoleCapabilities(currentSession)
    const userRoleCapabilities = getUserRoleCapabilities(currentSession)
    const capabilities = [
        ...organizationRoleCapabilities,
        ...userRoleCapabilities,
    ]
    capabilities.forEach((capability) => {
        const { action, subject, fields, condition, inverted } = capability
        if (inverted) {
            cannot(action, subject, fields, condition)
            return
        }
        can(action, subject, fields, condition)
    })
    return build({
        conditionsMatcher,
        resolveAction,
        fieldMatcher,
    })
}

export type PermissionMap = Partial<Record<Action, Subject>>

const isEmptyObject = (value: unknown) =>
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0

/**
 * Evaluates a route-style permission map ({ action: Subject }) against an
 * ability instance. When `payload` is provided it is bound as subject
 * conditions, same as the HTTP route preHandler does with `request.body`.
 * An empty object is checked like a missing payload: it says nothing about
 * the resource, and as a subject it has no field for a rule's conditions to
 * match, so a client that sent `{}` to a route taking no body (activate,
 * deactivate) was refused what the same request without a body got.
 * Returns the first denied pair, or null when every check passes.
 * Single source of truth for both the HTTP preHandler and the Platform MCP
 * tool handlers.
 */
export const findDeniedPermission = (
    abilityInstance: Pick<AppAbility, 'can'>,
    permissions: PermissionMap,
    payload?: unknown,
): { action: Action; subject: Subject } | null => {
    let action: Action
    for (action in permissions) {
        const actionSubject = permissions[action]
        if (!actionSubject) {
            continue
        }
        const target =
            payload && !isEmptyObject(payload)
                ? caslSubject(
                      actionSubject,
                      payload as Record<PropertyKey, unknown>,
                  )
                : actionSubject
        if (!abilityInstance.can(action, target)) {
            return { action, subject: actionSubject }
        }
    }
    return null
}

const ac = createAccessControl(defaultStatements)
const adminRole = ac.newRole(adminAc.statements)
const ownerRole = ac.newRole(ownerAc.statements)
const memberRole = ac.newRole(memberAc.statements)
const userRole = ac.newRole(memberAc.statements)

export const betterAuthOrganizationSettings = {
    ac,
    roles: {
        owner: ownerRole,
        admin: adminRole,
        member: memberRole,
        user: userRole,
    },
}

/**
 * Whether a user is currently banned. A ban with no expiry is permanent, one
 * with an expiry in the future is still in force, one already elapsed is not.
 * Every admission path (API key, Platform MCP, voice callback) must apply the
 * same rule: a ban that holds in one path and not another is a hole.
 */
export const isUserBanned = (
    user:
        | { banned?: boolean | null; banExpires?: Date | null }
        | null
        | undefined,
): boolean =>
    user?.banned === true &&
    (user.banExpires === null ||
        user.banExpires === undefined ||
        user.banExpires > new Date())

export * from './types'
export { subject } from '@casl/ability'
